// Leer un .zip por partes, sin cargarlo entero en memoria
// (ADR-2026-11-03-el-zip-se-lee-por-partes). El índice del .zip (el "directorio central") está al
// final del archivo: se lee eso, y después cada archivo que hace falta con `Blob.slice`, de a uno.
// Así un .zip de varios GB ocupa en memoria sólo lo que se saca de él. Soporta ZIP64 (los .zip de
// más de 4 GB o de más de 65.535 archivos) y los métodos que usan las computadoras al comprimir:
// guardado (0) y deflate (8).

import { inflate, inflateSync } from "fflate";

export interface EntradaDelZip {
  /** El nombre como lo decodifica fflate: UTF-8 si el .zip lo marca y, si no, latin1 (el
   *  "Comprimir" de Mac no pone la marca; lo arregla `repararNombreDeZip`). */
  nombre: string;
  /** Descomprimido, en bytes (del índice: no hace falta abrir el archivo para saberlo). */
  tamano: number;
  comprimido: number;
  metodo: number;
  cifrado: boolean;
  /** Dónde empieza su encabezado local. */
  offset: number;
}

/** No es un .zip, está cortado o es de varias partes (.z01, .z02…). */
export class ZipIlegible extends Error {}
/** Tiene contraseña, o una compresión que el navegador no abre (deflate64, bzip2, LZMA…). */
export class ZipNoSoportado extends Error {
  constructor(readonly motivo: "cifrado" | "metodo") {
    super(motivo);
  }
}

const FIN_DEL_INDICE = 0x06054b50;
const UBICADOR_ZIP64 = 0x07064b50;
const FIN_DEL_INDICE_ZIP64 = 0x06064b50;
const ENTRADA_DEL_INDICE = 0x02014b50;
const ENCABEZADO_LOCAL = 0x04034b50;
const MAX_U16 = 0xffff;
const MAX_U32 = 0xffffffff;
/** El fin del índice mide 22 bytes más un comentario de hasta 65.535. */
const MAX_COLA = 22 + MAX_U16;

const u16 = (b: Uint8Array, i: number) => b[i] | (b[i + 1] << 8);
const u32 = (b: Uint8Array, i: number) => (b[i] | (b[i + 1] << 8) | (b[i + 2] << 16) | (b[i + 3] << 24)) >>> 0;
const u64 = (b: Uint8Array, i: number) => u32(b, i) + u32(b, i + 4) * 2 ** 32;

async function leer(archivo: Blob, desde: number, largo: number): Promise<Uint8Array> {
  if (desde < 0 || largo < 0 || desde + largo > archivo.size) throw new ZipIlegible("fuera del archivo");
  return new Uint8Array(await archivo.slice(desde, desde + largo).arrayBuffer());
}

const latin1 = (b: Uint8Array): string => {
  let s = "";
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
  return s;
};

/** Dónde está y cuánto mide el índice, y cuántas entradas tiene (con ZIP64 si hace falta). */
async function ubicarIndice(zip: Blob): Promise<{ offset: number; largo: number; entradas: number }> {
  const desde = Math.max(0, zip.size - MAX_COLA);
  const cola = await leer(zip, desde, zip.size - desde);
  let fin = -1;
  for (let i = cola.length - 22; i >= 0; i--) {
    if (u32(cola, i) === FIN_DEL_INDICE && i + 22 + u16(cola, i + 20) <= cola.length) {
      fin = i;
      break;
    }
  }
  if (fin < 0) throw new ZipIlegible("sin fin del índice");
  if (u16(cola, fin + 4) !== 0 || u16(cola, fin + 6) !== 0) throw new ZipIlegible("zip de varias partes");
  let entradas = u16(cola, fin + 10);
  let largo = u32(cola, fin + 12);
  let offset = u32(cola, fin + 16);
  if (entradas === MAX_U16 || largo === MAX_U32 || offset === MAX_U32) {
    // ZIP64: el ubicador está justo antes del fin del índice y apunta al fin ZIP64.
    const posUbicador = desde + fin - 20;
    if (posUbicador < 0) throw new ZipIlegible("zip64 sin ubicador");
    const ubicador = await leer(zip, posUbicador, 20);
    if (u32(ubicador, 0) !== UBICADOR_ZIP64) throw new ZipIlegible("zip64 sin ubicador");
    const fin64 = await leer(zip, u64(ubicador, 8), 56);
    if (u32(fin64, 0) !== FIN_DEL_INDICE_ZIP64) throw new ZipIlegible("zip64 sin fin del índice");
    entradas = u64(fin64, 32);
    largo = u64(fin64, 40);
    offset = u64(fin64, 48);
  }
  return { offset, largo, entradas };
}

/** El índice del .zip: nombre, tamaño y dónde está cada archivo. No descomprime nada. */
export async function indiceDelZip(zip: Blob): Promise<EntradaDelZip[]> {
  const { offset, largo, entradas } = await ubicarIndice(zip);
  const indice = await leer(zip, offset, largo);
  const utf8 = new TextDecoder();
  const salida: EntradaDelZip[] = [];
  let i = 0;
  for (let n = 0; n < entradas; n++) {
    if (i + 46 > indice.length || u32(indice, i) !== ENTRADA_DEL_INDICE) throw new ZipIlegible("índice roto");
    const flags = u16(indice, i + 8);
    const largoNombre = u16(indice, i + 28);
    const largoExtra = u16(indice, i + 30);
    const largoComentario = u16(indice, i + 32);
    let comprimido = u32(indice, i + 20);
    let tamano = u32(indice, i + 24);
    let local = u32(indice, i + 42);
    const bytesDelNombre = indice.subarray(i + 46, i + 46 + largoNombre);
    // Campo ZIP64 (0x0001): trae, en este orden, sólo los valores que no entraron en 32 bits.
    let e = i + 46 + largoNombre;
    const finExtra = e + largoExtra;
    while (e + 4 <= finExtra) {
      const id = u16(indice, e);
      const tam = u16(indice, e + 2);
      if (id === 0x0001) {
        let k = e + 4;
        if (tamano === MAX_U32) {
          tamano = u64(indice, k);
          k += 8;
        }
        if (comprimido === MAX_U32) {
          comprimido = u64(indice, k);
          k += 8;
        }
        if (local === MAX_U32) local = u64(indice, k);
        break;
      }
      e += 4 + tam;
    }
    salida.push({
      nombre: flags & 0x800 ? utf8.decode(bytesDelNombre) : latin1(bytesDelNombre),
      tamano,
      comprimido,
      metodo: u16(indice, i + 10),
      cifrado: (flags & 1) === 1,
      offset: local,
    });
    i = finExtra + largoComentario;
  }
  return salida;
}

/** Saca UN archivo del .zip: lee sólo sus bytes y los descomprime. */
export async function extraerDelZip(zip: Blob, entrada: EntradaDelZip): Promise<Uint8Array> {
  if (entrada.cifrado) throw new ZipNoSoportado("cifrado");
  if (entrada.metodo !== 0 && entrada.metodo !== 8) throw new ZipNoSoportado("metodo");
  // El encabezado local puede tener un "extra" distinto del índice: su largo manda.
  const local = await leer(zip, entrada.offset, 30);
  if (u32(local, 0) !== ENCABEZADO_LOCAL) throw new ZipIlegible("encabezado local roto");
  const datos = entrada.offset + 30 + u16(local, 26) + u16(local, 28);
  const crudo = await leer(zip, datos, entrada.comprimido);
  if (entrada.metodo === 0) return crudo;
  try {
    return await descomprimir(crudo, entrada.tamano);
  } catch {
    throw new ZipIlegible("no se pudo descomprimir");
  }
}

/** Lo grande y que comprime bien se descomprime en un worker: en el hilo principal congela la
 *  página mientras dura. Es el mismo criterio del `unzip` de fflate que usaba el lector anterior:
 *  menos de 512 KB, o casi sin comprimir (un JPG: copiar bytes), va en el momento. Sin `Worker`
 *  (los tests corren en Node), también. */
function descomprimir(crudo: Uint8Array, tamano: number): Promise<Uint8Array> {
  if (tamano < 512 * 1024 || crudo.length > 0.8 * tamano || typeof Worker === "undefined")
    return Promise.resolve(inflateSync(crudo, { out: new Uint8Array(tamano) }));
  return new Promise((resolve, reject) =>
    inflate(crudo, { size: tamano }, (err, datos) => (err ? reject(err) : resolve(datos))),
  );
}
